<script lang="ts" setup>
import ChevronDownIcon from '~/assets/icons/ui/ChevronDown.svg'

const props = defineProps<{
  currentPath: string
  isCollapsed?: boolean
  isMobile?: boolean
}>()
const emit = defineEmits<{ (e: 'select'): void }>()
const route = useRoute()

const { isCityAccount, isHeatProtectionAccount, isInfectionProtectionAccount } = useAuthorization()

const initialSection = computed(() => {
  if (!isCityAccount.value) {
    return undefined
  }
  return deriveMenuSectionFromPath(route.path)
})

const selectedSection = ref<MenuSection | undefined>(initialSection.value)

const selectOptions = [
  ...(isInfectionProtectionAccount.value ? [{ key: 'infectionProtection', label: 'Kölner Infektionsschutz online', value: 'infectionProtection' }] : []),
  ...(isHeatProtectionAccount.value ? [{ key: 'heatProtection', label: 'Hitzeschutz', value: 'heatProtection' }] : []),
  { key: 'administration', label: 'Administration', value: 'administration' },
]

const { menuOptions, footerOptions } = useMenu(selectedSection)
const { pageTitle, currentRoute } = useRouteTitle(computed(() => props.currentPath))

const logoWidth = computed(() => {
  if (props.isCollapsed) {
    return '50px'
  }
  return props.isMobile ? '200px' : '225px'
})

useHead({
  title: pageTitle,
})
</script>

<template>
  <div class="flex flex-col justify-between h-full">
    <div class="shrink-0 ">
      <div class="flex flex-col justify-center pt-5 pb-2">
        <TheLogo
          :show-icon="isCollapsed"
          :show-logo="!isCollapsed"
          :max-width="logoWidth"
        />
        <template v-if="!isCollapsed">
          <div v-if="isCityAccount">
            <n-dropdown
              :options="selectOptions"
              @select="(value) => selectedSection = value"
            >
              <div class="w-full flex justify-between  items-center py-2 px-2 mb-1.5 bg-gray-100 border-y">
                <div class="w-full flex justify-center whitespace-nowrap text-black tracking-wide font-semibold" style="font-size: 16px;">
                  {{ $t(`app.section.${selectedSection}.title`) }}
                </div>
                <div class="h-4 w-4">
                  <ChevronDownIcon />
                </div>
              </div>
            </n-dropdown>
          </div>
          <div v-else class="w-full flex flex-col items-center py-2 mb-1.5 bg-gray-100 border-y">
            <p class="whitespace-nowrap text-black tracking-wide font-semibold" style="font-size: 16px;">
              {{ $t('app.title') }}
            </p>
          </div>
        </template>
      </div>
      <n-menu
        :options="menuOptions"
        :value="currentRoute"
        :collapsed="isCollapsed"
        :collapsed-width="75"
        :collapsed-icon-size="22"
        :root-indent="20"
        :indent="10"
        accordion
        :class="isCollapsed ? 'mt-10' : ''"
        @update:value="emit('select')"
      />
    </div>
    <div>
      <n-menu
        :options="footerOptions"
        :value="currentRoute"
        :collapsed="isCollapsed"
        :collapsed-width="70"
        :collapsed-icon-size="22"
        :root-indent="20"
        :render-icon="({ icon }) => (icon && isCollapsed) && icon()"
        accordion
        :theme-overrides="{ itemHeight: '35px' }"
        class="shrink-0"
      />
      <TheLanguageSelector
        class="mb-5 mx-3 md:mb-3 md:mx-4"
        :class="isCollapsed && 'flex justify-center'"
        :component="isCollapsed ? 'dropdown' : 'select'"
      />
    </div>
  </div>
</template>

<style>
.n-dropdown-option-body__prefix--show-icon {
  padding: 5px;
  padding-left: 10px;
  padding-right: 8px;
}
</style>
